// This file deals with Marketplace API calls for Stripe Custom Connect accounts.
// Bank/external account details are collected by Stripe hosted onboarding only.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import {
  STRIPE_CONNECT_ONBOARDING_COLLECTION_OPTIONS,
  STRIPE_CONNECT_ONBOARDING_LINK_TYPE,
  STRIPE_CONNECT_REQUIRED_CAPABILITIES,
} from '../util/stripeConnect';

export { STRIPE_CONNECT_REQUIRED_CAPABILITIES };

// ================ Async thunks ================ //

///////////////////////////
// Create Stripe Account //
///////////////////////////
const createStripeAccountPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  if (typeof window === 'undefined' || !window.Stripe) {
    throw new Error('Stripe must be loaded for submitting PayoutPreferences');
  }
  const {
    country,
    accountType,
    businessProfileMCC,
    businessProfileURL,
    stripePublishableKey,
  } = params;
  const stripe = window.Stripe(stripePublishableKey);

  // Capabilities are a collection of settings that can be requested for each provider.
  // What Capabilities are required determines what information Stripe requires to be
  // collected from the providers.
  // You can read more from here: https://stripe.com/docs/connect/capabilities-overview
  // Note: with default processes, both 'card_payments' and 'transfers' are required.
  const requestedCapabilities = STRIPE_CONNECT_REQUIRED_CAPABILITIES;

  const accountInfo = {
    business_type: accountType,
    tos_shown_and_accepted: true,
  };

  return stripe
    .createToken('account', accountInfo)
    .then(response => {
      const accountToken = response.token.id;
      return sdk.stripeAccount.create(
        {
          country,
          accountToken,
          requestedCapabilities,
          businessProfileMCC,
          businessProfileURL,
        },
        { expand: true }
      );
    })
    .then(response => {
      const stripeAccount = response.data.data;
      return stripeAccount;
    })
    .catch(err => {
      const e = storableError(err);
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'create-stripe-account-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const createStripeAccountThunk = createAsyncThunk(
  'stripeConnectAccount/createStripeAccount',
  createStripeAccountPayloadCreator
);
// Backward compatible wrapper function
export const createStripeAccount = params => dispatch => {
  return dispatch(createStripeAccountThunk(params)).unwrap();
};

///////////////////////////
// Update Stripe Account //
///////////////////////////
const updateStripeAccountPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  // Keep requested capabilities in sync for existing accounts. Missing business,
  // verification, and external-account requirements are completed through Stripe
  // hosted onboarding/account links, not custom Tutulist bank forms.
  return sdk.stripeAccount
    .update({ requestedCapabilities: STRIPE_CONNECT_REQUIRED_CAPABILITIES }, { expand: true })
    .then(response => {
      const stripeAccount = response.data.data;
      return stripeAccount;
    })
    .catch(err => {
      const e = storableError(err);
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'update-stripe-account-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const updateStripeAccountThunk = createAsyncThunk(
  'stripeConnectAccount/updateStripeAccount',
  updateStripeAccountPayloadCreator
);
// Backward compatible wrapper function
export const updateStripeAccount = params => dispatch => {
  return dispatch(updateStripeAccountThunk(params)).unwrap();
};

//////////////////////////
// Fetch Stripe Account //
//////////////////////////
const fetchStripeAccountPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  return sdk.stripeAccount
    .fetch()
    .then(response => {
      const stripeAccount = response.data.data;
      return stripeAccount;
    })
    .catch(err => {
      const e = storableError(err);
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'fetch-stripe-account-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const fetchStripeAccountThunk = createAsyncThunk(
  'stripeConnectAccount/fetchStripeAccount',
  fetchStripeAccountPayloadCreator
);
// Backward compatible wrapper function
export const fetchStripeAccount = params => dispatch => {
  return dispatch(fetchStripeAccountThunk(params)).unwrap();
};

/////////////////////////////////////
// Get Stripe Connect Account Link //
/////////////////////////////////////
const getStripeConnectAccountLinkPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  const { failureURL, successURL, type, missingRequirementTypes = [] } = params;
  const isExternalAccountMissing = missingRequirementTypes.includes('externalAccount');

  if (type === STRIPE_CONNECT_ONBOARDING_LINK_TYPE && isExternalAccountMissing) {
    log.warn(
      new Error('Stripe external account is still missing before hosted onboarding redirect'),
      'stripe-external-account-onboarding-link-created',
      {
        linkType: type,
        collectionOptions: STRIPE_CONNECT_ONBOARDING_COLLECTION_OPTIONS,
        operatorNote:
          'If Stripe still shows only a verification summary, check Stripe Dashboard > Connect settings > Payout onboarding / External account settings and enable external account collection for connected accounts.',
      }
    );
  }

  // Read more from collection_options and verification updates from Stripe's Docs:
  // https://docs.stripe.com/api/account_links/create
  return sdk.stripeAccountLinks
    .create({
      failureURL,
      successURL,
      type,
      collectionOptions: STRIPE_CONNECT_ONBOARDING_COLLECTION_OPTIONS,
    })
    .then(response => {
      // Return the account link
      return response.data.data.attributes.url;
    })
    .catch(err => {
      const e = storableError(err);
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'get-stripe-account-link-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const getStripeConnectAccountLinkThunk = createAsyncThunk(
  'stripeConnectAccount/getStripeConnectAccountLink',
  getStripeConnectAccountLinkPayloadCreator
);
// Backward compatible wrapper function
export const getStripeConnectAccountLink = params => dispatch => {
  return dispatch(getStripeConnectAccountLinkThunk(params)).unwrap();
};

// ================ Slice ================ //

const stripeConnectAccountSlice = createSlice({
  name: 'stripeConnectAccount',
  initialState: {
    createStripeAccountInProgress: false,
    createStripeAccountError: null,
    updateStripeAccountInProgress: false,
    updateStripeAccountError: null,
    fetchStripeAccountInProgress: false,
    fetchStripeAccountError: null,
    getAccountLinkInProgress: false,
    getAccountLinkError: null,
    stripeAccount: null,
    stripeAccountFetched: false,
  },
  reducers: {
    stripeAccountClearError: state => {
      return {
        createStripeAccountInProgress: false,
        createStripeAccountError: null,
        updateStripeAccountInProgress: false,
        updateStripeAccountError: null,
        fetchStripeAccountInProgress: false,
        fetchStripeAccountError: null,
        getAccountLinkInProgress: false,
        getAccountLinkError: null,
        stripeAccount: null,
        stripeAccountFetched: false,
      };
    },
    updateStripeConnectAccount: (state, action) => {
      state.stripeAccount = action.payload;
      state.stripeAccountFetched = true;
    },
  },
  extraReducers: builder => {
    builder
      // Create Stripe Account cases
      .addCase(createStripeAccountThunk.pending, state => {
        state.createStripeAccountError = null;
        state.createStripeAccountInProgress = true;
      })
      .addCase(createStripeAccountThunk.fulfilled, (state, action) => {
        state.createStripeAccountInProgress = false;
        state.stripeAccount = action.payload;
        state.stripeAccountFetched = true;
      })
      .addCase(createStripeAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.createStripeAccountError = action.payload;
        state.createStripeAccountInProgress = false;
      })
      // Update Stripe Account cases
      .addCase(updateStripeAccountThunk.pending, state => {
        state.updateStripeAccountError = null;
        state.updateStripeAccountInProgress = true;
      })
      .addCase(updateStripeAccountThunk.fulfilled, (state, action) => {
        state.updateStripeAccountInProgress = false;
        state.stripeAccount = action.payload;
        state.stripeAccountFetched = true;
      })
      .addCase(updateStripeAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.updateStripeAccountError = action.payload;
        state.updateStripeAccountInProgress = false;
      })
      // Fetch Stripe Account cases
      .addCase(fetchStripeAccountThunk.pending, state => {
        state.fetchStripeAccountError = null;
        state.fetchStripeAccountInProgress = true;
      })
      .addCase(fetchStripeAccountThunk.fulfilled, (state, action) => {
        state.fetchStripeAccountInProgress = false;
        state.stripeAccount = action.payload;
        state.stripeAccountFetched = true;
      })
      .addCase(fetchStripeAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.fetchStripeAccountError = action.payload;
        state.fetchStripeAccountInProgress = false;
      })
      // Get Account Link cases
      .addCase(getStripeConnectAccountLinkThunk.pending, state => {
        state.getAccountLinkError = null;
        state.getAccountLinkInProgress = true;
      })
      .addCase(getStripeConnectAccountLinkThunk.fulfilled, state => {
        state.getAccountLinkInProgress = false;
      })
      .addCase(getStripeConnectAccountLinkThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.getAccountLinkInProgress = false;
        state.getAccountLinkError = action.payload;
      });
  },
});

// Export the action creators
export const {
  stripeAccountClearError,
  updateStripeConnectAccount,
} = stripeConnectAccountSlice.actions;

export default stripeConnectAccountSlice.reducer;
